import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../service/auth.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private isRefreshing = false;
  private currentToken: string | null = null; // <-- Variable globale pour le token

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Utilise le token global si disponible, sinon celui du service
    const token = this.currentToken || this.authService.getToken();

    if (token) {
      request = this.addTokenToRequest(request, token);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.authService.getRefreshToken()) {
      this.authService.logout();
      return throwError(() => new Error('No refresh token'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.currentToken = null;

      return this.authService.refreshToken().pipe(
        switchMap(({ token }) => {
          // 1. Mise à jour synchrone AVANT de rejouer la requête
          this.currentToken = token;
          this.authService.saveToken(token);

          // 2. Nouvelle instance de la requête ORIGINALE
          const newRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });

          this.isRefreshing = false;
          return next.handle(newRequest); // 3. Rejeu avec NOUVEAU token
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.authService.logout();
          return throwError(() => error);
        }),
      );
    }

    // Solution alternative pour les requêtes concurrentes
    return this.waitForTokenRefresh().pipe(
      switchMap(() => {
        const newRequest = this.addTokenToRequest(request, this.currentToken!);
        return next.handle(newRequest);
      }),
    );
  }

  private waitForTokenRefresh(): Observable<void> {
    return new Observable((observer) => {
      const checkInterval = setInterval(() => {
        if (!this.isRefreshing) {
          clearInterval(checkInterval);
          observer.next();
          observer.complete();
        }
      }, 100);
    });
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
